import { IBundleItem } from "@/api/AppApi/services/product/interfaces/product-bundle-types";
import {
  IGroupedBundleItems,
  ISelectedBundleItems,
} from "@/hooks/product-has-attrs/product-has-attrs-helpers/interfaces";

// Helper function to generate repeated items for configurable products
export const generateBundleItems = (bundleItem: IBundleItem): IBundleItem[] => {
  if (bundleItem.product.is_configurable && bundleItem.quantity > 1) {
    // Create multiple items with quantity 1 for configurable products
    return Array.from({ length: bundleItem.quantity }, () => ({
      ...bundleItem,
      quantity: 1,
    }));
  }
  return [bundleItem];
};

export const groupBundleItemsByBundleItemId = (
  selectedBundleItems: ISelectedBundleItems[],
): IGroupedBundleItems[] => {
  const groupedMap = new Map<number, IGroupedBundleItems>();

  selectedBundleItems.forEach((item) => {
    const existingGroup = groupedMap.get(item.bundle_item_id);

    if (existingGroup) {
      if (item.is_configurable && existingGroup.children && item.child) {
        existingGroup.children.push(item.child);
      }
    } else {
      const newGroup: IGroupedBundleItems = {
        bundle_item_id: item.bundle_item_id,
        product_id: item.product_id,
        is_configurable: item.is_configurable,
      };
      if (item.is_configurable && item.child) {
        newGroup.children = [item.child];
      }

      groupedMap.set(item.bundle_item_id, newGroup);
    }
  });

  return Array.from(groupedMap.values());
};

export const ungroupBundleItems = (
  groupedBundleItems: IGroupedBundleItems[],
): ISelectedBundleItems[] => {
  const result: ISelectedBundleItems[] = [];

  groupedBundleItems.forEach((groupedItem) => {
    if (groupedItem.is_configurable && groupedItem.children) {
      // For configurable items, create one item for each child
      groupedItem.children.forEach((child) => {
        result.push({
          bundle_item_id: groupedItem.bundle_item_id,
          product_id: groupedItem.product_id,
          is_configurable: groupedItem.is_configurable,
          child: child,
        });
      });
    } else {
      // For non-configurable items, create a single item with null child
      result.push({
        bundle_item_id: groupedItem.bundle_item_id,
        product_id: groupedItem.product_id,
        is_configurable: groupedItem.is_configurable,
        child: null,
      });
    }
  });

  return result;
};

/**
 * Compares two arrays of IGroupedBundleItems to check if they are the same
 * @param bundleItems1 - First array of grouped bundle items
 * @param bundleItems2 - Second array of grouped bundle items
 * @returns boolean indicating if the arrays are equal
 */
export const compareBundleItems = (
  bundleItems1: IGroupedBundleItems[],
  bundleItems2: IGroupedBundleItems[],
): boolean => {
  if (bundleItems1.length !== bundleItems2.length) {
    return false;
  }

  // Sort both arrays by bundle_item_id for consistent comparison
  const sorted1 = [...bundleItems1].sort((a, b) => a.bundle_item_id - b.bundle_item_id);
  const sorted2 = [...bundleItems2].sort((a, b) => a.bundle_item_id - b.bundle_item_id);

  return sorted1.every((item1, index) => {
    const item2 = sorted2[index];
    
    // Compare basic properties
    if (
      item1.bundle_item_id !== item2.bundle_item_id ||
      item1.product_id !== item2.product_id ||
      item1.is_configurable !== item2.is_configurable
    ) {
      return false;
    }

    // Compare children arrays
    if (item1.is_configurable && item2.is_configurable) {
      const children1 = item1.children || [];
      const children2 = item2.children || [];
      
      if (children1.length !== children2.length) {
        return false;
      }
      
      // Sort children for consistent comparison
      const sortedChildren1 = [...children1].sort((a, b) => (a || 0) - (b || 0));
      const sortedChildren2 = [...children2].sort((a, b) => (a || 0) - (b || 0));
      
      return sortedChildren1.every((child, childIndex) => 
        child === sortedChildren2[childIndex]
      );
    }

    return true;
  });
};
